import { useState, useMemo } from "react";
import BotonMapaProceso from "./BotonMapaProceso";
import { subirDatos, eliminarDato } from "./syncSupabase";

const Q = {
  azul:"#1C3FAA", azulVivo:"#2A7FFF", azulMedio:"#6B6FD6",
  grisClaro:"#E8E9F6", grisPalido:"#F4F6FB", grisOscuro:"#2B2F36",
  grisPlata:"#8E93A1", blanco:"#FFFFFF",
  verde:"#16a34a", verdeClaro:"#22c55e",
};

const STORAGE_CONSUMOS = 'alqos_sust_consumos';
const STORAGE_RESIDUOS = 'alqos_sust_residuos';
const STORAGE_OBJETIVOS = 'alqos_sust_objetivos';
const STORAGE_ASPECTOS = 'alqos_sust_aspectos';
const STORAGE_LEGAL = 'alqos_sust_legal';
const STORAGE_CONFIG = 'alqos_sust_config';

const storage = {
  get: (k, def=[]) => { try { return JSON.parse(localStorage.getItem(k)||JSON.stringify(def)); } catch { return def; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch(e) { console.error(e); } },
};

const CONSUMOS_DEFAULT = [
  {id:"energia", nombre:"Energía eléctrica", unidad:"kWh", factorCO2:0.369, icono:"⚡", activo:true},
  {id:"gas",     nombre:"Gas natural",       unidad:"m³",  factorCO2:2.1,   icono:"🔥", activo:true},
  {id:"agua",    nombre:"Agua",              unidad:"m³",  factorCO2:0,     icono:"💧", activo:true},
  {id:"gasoil",  nombre:"Gasoil",            unidad:"L",   factorCO2:2.68,  icono:"⛽", activo:true},
];

const RESIDUOS_DEFAULT = [
  {id:"papel",    nombre:"Papel / Cartón / Cartulina", tipo:"reciclable",  color:"#3b82f6", activo:true},
  {id:"scrap",    nombre:"Scrap de impresión",          tipo:"reciclable",  color:"#f59e0b", activo:true},
  {id:"tintas",   nombre:"Tintas y solventes",          tipo:"peligroso",   color:"#e63946", activo:true},
  {id:"plastico", nombre:"Plástico",                    tipo:"reciclable",  color:"#22c55e", activo:true},
  {id:"filmado",  nombre:"Film / Laminado",             tipo:"especial",    color:"#7c3aed", activo:true},
  {id:"quimicos", nombre:"Químicos / Reveladores",      tipo:"peligroso",   color:"#dc2626", activo:true},
];

const DESTINOS = ["Reciclaje", "Valorización energética", "Disposición final", "Operador habilitado", "Retiro por proveedor"];
const SIGNIFICATIVIDAD = ["Alta", "Media", "Baja"];
const ESTADOS_LEGAL = {
  cumple:    {label:"✅ Cumple",    color:"#22c55e"},
  parcial:   {label:"🟡 Parcial",  color:"#f59e0b"},
  incumple:  {label:"🔴 Incumple", color:"#e63946"},
  na:        {label:"➖ N/A",      color:"#808085"},
};

function mesActual() { return new Date().toISOString().slice(0,7); }
function fechaHoy()  { return new Date().toISOString().slice(0,10); }
function anioActual(){ return new Date().getFullYear(); }

function calcularCO2(consumos, registros) {
  return registros.reduce((total, r) => {
    const config = consumos.find(c => c.id === r.consumoId);
    if (!config) return total;
    return total + (Number(r.cantidad)||0) * config.factorCO2;
  }, 0);
}

export default function Sustentabilidad({ onVolver, oscuro, usuario }) {
  const [tab, setTab] = useState("dashboard");

  // Config dinámica de consumos y residuos
  const [configConsumos, setConfigConsumos] = useState(() => storage.get(STORAGE_CONFIG+'_consumos', CONSUMOS_DEFAULT));
  const [configResiduos, setConfigResiduos] = useState(() => storage.get(STORAGE_CONFIG+'_residuos', RESIDUOS_DEFAULT));

  // Datos
  const [registrosConsumo, setRegistrosConsumo] = useState(() => storage.get(STORAGE_CONSUMOS));
  const [registrosResiduo, setRegistrosResiduo] = useState(() => storage.get(STORAGE_RESIDUOS));
  const [objetivos,        setObjetivos]        = useState(() => storage.get(STORAGE_OBJETIVOS));
  const [aspectos,         setAspectos]         = useState(() => storage.get(STORAGE_ASPECTOS));
  const [legales,          setLegales]          = useState(() => storage.get(STORAGE_LEGAL));

  // Forms
  const [formConsumo,  setFormConsumo]  = useState({ mes: mesActual() });
  const [formResiduo,  setFormResiduo]  = useState({ fecha: fechaHoy(), destino: DESTINOS[0] });
  const [formObjetivo, setFormObjetivo] = useState({ anio: anioActual(), progreso: 0 });
  const [formAspecto,  setFormAspecto]  = useState({ significatividad: "Media" });
  const [formLegal,    setFormLegal]    = useState({ estado: "cumple", fechaVerif: fechaHoy() });
  const [formNuevoConsumo, setFormNuevoConsumo] = useState({});
  const [formNuevoResiduo, setFormNuevoResiduo] = useState({});

  // Vistas
  const [vistaConsumo,  setVistaConsumo]  = useState("listado");
  const [vistaResiduo,  setVistaResiduo]  = useState("listado");
  const [vistaConfig,   setVistaConfig]   = useState(false);

  const esAdmin = usuario?.rol === "Administrador";

  const bg      = oscuro ? "#09184D" : Q.grisPalido;
  const cardBg  = oscuro ? "#0d2160" : Q.blanco;
  const texto   = oscuro ? "#EDF2FA" : Q.grisOscuro;
  const borde   = oscuro ? "#1e3a8a" : Q.grisClaro;
  const navBg   = oscuro ? "#060f30" : Q.azul;
  const subtexto= oscuro ? "#9aabd4" : Q.grisPlata;
  const card    = {background:cardBg, borderRadius:"14px", padding:"1.5rem", border:`1px solid ${borde}`, marginBottom:"14px"};
  const inp     = {width:"100%", padding:"9px 12px", borderRadius:"10px", border:`1.5px solid ${borde}`, fontSize:"13px", boxSizing:"border-box", marginTop:"4px", background:cardBg, color:texto, fontFamily:"sans-serif", outline:"none"};
  const lbl     = {fontSize:"11px", color:subtexto, fontWeight:"700", display:"block", marginTop:"12px", letterSpacing:".05em", textTransform:"uppercase"};

  // ── KPIs ──────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const mesHoy = mesActual();
    const consumosMes = registrosConsumo.filter(r => r.mes === mesHoy);
    const residuosMes = registrosResiduo.filter(r => r.fecha?.startsWith(mesHoy));
    const co2Mes = calcularCO2(configConsumos, consumosMes);
    const totalResiduosMes = residuosMes.reduce((a,r) => a+(Number(r.cantidad)||0), 0);
    const residuosPeligrosos = residuosMes.filter(r => {
      const cfg = configResiduos.find(c => c.id === r.residuoId);
      return cfg?.tipo === "peligroso";
    }).reduce((a,r) => a+(Number(r.cantidad)||0), 0);
    const objetivosActivos = objetivos.filter(o => o.anio === anioActual());
    const objetivosEnRiesgo = objetivosActivos.filter(o => Number(o.progreso) < 50).length;
    const cumplimientoLegal = legales.length > 0
      ? Math.round((legales.filter(l => l.estado === "cumple").length / legales.length) * 100)
      : 100;
    return { co2Mes: Math.round(co2Mes*10)/10, totalResiduosMes: Math.round(totalResiduosMes*10)/10, residuosPeligrosos: Math.round(residuosPeligrosos*10)/10, objetivosEnRiesgo, cumplimientoLegal };
  }, [registrosConsumo, registrosResiduo, objetivos, legales, configConsumos, configResiduos]);

  // ── GUARDAR CONSUMO ───────────────────────────────────────
  const guardarConsumo = () => {
    if (!formConsumo.consumoId || !formConsumo.cantidad) { alert("Completá tipo y cantidad."); return; }
    const nuevo = { ...formConsumo, id: Date.now(), fechaCarga: new Date().toISOString(), cargadoPor: usuario?.nombre };
    const nuevos = [...registrosConsumo, nuevo];
    setRegistrosConsumo(nuevos);
    storage.set(STORAGE_CONSUMOS, nuevos);
    subirDatos(STORAGE_CONSUMOS);
    setFormConsumo({ mes: mesActual() });
    setVistaConsumo("listado");
  };

  // ── GUARDAR RESIDUO ───────────────────────────────────────
  const guardarResiduo = () => {
    if (!formResiduo.residuoId || !formResiduo.cantidad) { alert("Completá tipo y cantidad."); return; }
    const nuevo = { ...formResiduo, id: Date.now(), fechaCarga: new Date().toISOString(), cargadoPor: usuario?.nombre };
    const nuevos = [...registrosResiduo, nuevo];
    setRegistrosResiduo(nuevos);
    storage.set(STORAGE_RESIDUOS, nuevos);
    subirDatos(STORAGE_RESIDUOS);
    setFormResiduo({ fecha: fechaHoy(), destino: DESTINOS[0] });
    setVistaResiduo("listado");
  };

  // ── GUARDAR OBJETIVO ──────────────────────────────────────
  const guardarObjetivo = () => {
    if (!formObjetivo.descripcion?.trim()) { alert("Describí el objetivo."); return; }
    const nuevo = { ...formObjetivo, id: Date.now(), creadoPor: usuario?.nombre };
    const nuevos = [...objetivos, nuevo];
    setObjetivos(nuevos);
    storage.set(STORAGE_OBJETIVOS, nuevos);
    subirDatos(STORAGE_OBJETIVOS);
    setFormObjetivo({ anio: anioActual(), progreso: 0 });
  };

  const actualizarProgresoObj = (id, progreso) => {
    const act = objetivos.map(o => o.id === id ? { ...o, progreso: Number(progreso) } : o);
    setObjetivos(act);
    storage.set(STORAGE_OBJETIVOS, act);
    subirDatos(STORAGE_OBJETIVOS);
  };

  const eliminarObjetivo = async (id) => {
    const act = objetivos.filter(o => o.id !== id);
    setObjetivos(act);
    storage.set(STORAGE_OBJETIVOS, act);
    await eliminarDato(STORAGE_OBJETIVOS, id);
  };

  // ── GUARDAR ASPECTO ───────────────────────────────────────
  const guardarAspecto = () => {
    if (!formAspecto.aspecto?.trim()) { alert("Describí el aspecto ambiental."); return; }
    const nuevo = { ...formAspecto, id: Date.now(), fechaRevision: fechaHoy(), creadoPor: usuario?.nombre };
    const nuevos = [...aspectos, nuevo];
    setAspectos(nuevos);
    storage.set(STORAGE_ASPECTOS, nuevos);
    subirDatos(STORAGE_ASPECTOS);
    setFormAspecto({ significatividad: "Media" });
  };

  const eliminarAspecto = async (id) => {
    const act = aspectos.filter(a => a.id !== id);
    setAspectos(act);
    storage.set(STORAGE_ASPECTOS, act);
    await eliminarDato(STORAGE_ASPECTOS, id);
  };

  // ── GUARDAR LEGAL ─────────────────────────────────────────
  const guardarLegal = () => {
    if (!formLegal.requisito?.trim()) { alert("Describí el requisito legal."); return; }
    const nuevo = { ...formLegal, id: Date.now(), creadoPor: usuario?.nombre };
    const nuevos = [...legales, nuevo];
    setLegales(nuevos);
    storage.set(STORAGE_LEGAL, nuevos);
    subirDatos(STORAGE_LEGAL);
    setFormLegal({ estado: "cumple", fechaVerif: fechaHoy() });
  };

  const cambiarEstadoLegal = (id, estado) => {
    const act = legales.map(l => l.id === id ? { ...l, estado, fechaVerif: fechaHoy(), verificadoPor: usuario?.nombre } : l);
    setLegales(act);
    storage.set(STORAGE_LEGAL, act);
    subirDatos(STORAGE_LEGAL);
  };

  const eliminarLegal = async (id) => {
    const act = legales.filter(l => l.id !== id);
    setLegales(act);
    storage.set(STORAGE_LEGAL, act);
    await eliminarDato(STORAGE_LEGAL, id);
  };

  // ── CONFIG CONSUMOS ───────────────────────────────────────
  const agregarConsumo = () => {
    if (!formNuevoConsumo.nombre?.trim()) { alert("Nombre requerido."); return; }
    const nuevo = { id: Date.now().toString(), activo: true, factorCO2: 0, ...formNuevoConsumo };
    const nuevos = [...configConsumos, nuevo];
    setConfigConsumos(nuevos);
    storage.set(STORAGE_CONFIG+'_consumos', nuevos);
    subirDatos(STORAGE_CONFIG+'_consumos');
    setFormNuevoConsumo({});
  };

  const toggleConsumo = (id) => {
    const nuevos = configConsumos.map(c => c.id === id ? { ...c, activo: !c.activo } : c);
    setConfigConsumos(nuevos);
    storage.set(STORAGE_CONFIG+'_consumos', nuevos);
    subirDatos(STORAGE_CONFIG+'_consumos');
  };

  const eliminarConsumoConfig = async (id) => {
    const nuevos = configConsumos.filter(c => c.id !== id);
    setConfigConsumos(nuevos);
    storage.set(STORAGE_CONFIG+'_consumos', nuevos);
    await eliminarDato(STORAGE_CONFIG+'_consumos', id);
  };

  // ── CONFIG RESIDUOS ───────────────────────────────────────
  const agregarResiduo = () => {
    if (!formNuevoResiduo.nombre?.trim()) { alert("Nombre requerido."); return; }
    const nuevo = { id: Date.now().toString(), activo: true, tipo: "reciclable", color: "#3b82f6", ...formNuevoResiduo };
    const nuevos = [...configResiduos, nuevo];
    setConfigResiduos(nuevos);
    storage.set(STORAGE_CONFIG+'_residuos', nuevos);
    subirDatos(STORAGE_CONFIG+'_residuos');
    setFormNuevoResiduo({});
  };

  const toggleResiduo = (id) => {
    const nuevos = configResiduos.map(r => r.id === id ? { ...r, activo: !r.activo } : r);
    setConfigResiduos(nuevos);
    storage.set(STORAGE_CONFIG+'_residuos', nuevos);
    subirDatos(STORAGE_CONFIG+'_residuos');
  };

  const eliminarResiduoConfig = async (id) => {
    const nuevos = configResiduos.filter(r => r.id !== id);
    setConfigResiduos(nuevos);
    storage.set(STORAGE_CONFIG+'_residuos', nuevos);
    await eliminarDato(STORAGE_CONFIG+'_residuos', id);
  };

  // ── RENDER ────────────────────────────────────────────────
  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"sans-serif" }}>

      {/* NAVBAR */}
      <div style={{ background:navBg, padding:"1rem 1.5rem", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:100, boxShadow:`0 2px 12px ${Q.azul}40` }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <button onClick={onVolver} style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"white", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"13px" }}>← Menú</button>
          <span style={{ color:"white", fontWeight:"700", fontSize:"16px" }}>🌿 Sustentabilidad y MA</span>
        </div>
        {esAdmin && (
          <button onClick={() => setVistaConfig(v => !v)}
            style={{ padding:"6px 12px", background:vistaConfig?"rgba(22,163,74,0.3)":"rgba(255,255,255,0.1)", border:`1px solid ${vistaConfig?"rgba(22,163,74,0.5)":"rgba(255,255,255,0.2)"}`, color:"white", borderRadius:"8px", cursor:"pointer", fontSize:"12px", fontWeight:"600" }}>
            ⚙️ Configurar
          </button>
        )}
        {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="sustentabilidad" oscuro={oscuro} />}
      </div>

      <div style={{ padding:"1.2rem 1.5rem", maxWidth:"860px", margin:"0 auto" }}>

        {/* ── PANEL CONFIG ─────────────────────────────────── */}
        {vistaConfig && (
          <div style={{ ...card, background: oscuro?"#0a0a2e":"#f0fdf4", border:`1.5px solid ${Q.verdeClaro}40`, marginBottom:"1.5rem" }}>
            <h3 style={{ fontSize:"14px", fontWeight:"800", color:Q.verde, marginBottom:"16px" }}>⚙️ Configuración de consumos y residuos</h3>

            {/* Consumos */}
            <p style={{ fontSize:"12px", fontWeight:"700", color:subtexto, textTransform:"uppercase", letterSpacing:".1em", margin:"0 0 10px" }}>Tipos de consumo</p>
            {configConsumos.map(c => (
              <div key={c.id} style={{ display:"flex", alignItems:"center", gap:"8px", padding:"8px 12px", background:cardBg, borderRadius:"10px", marginBottom:"6px", border:`1px solid ${borde}` }}>
                <span style={{ fontSize:"16px" }}>{c.icono||"📊"}</span>
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:"13px", fontWeight:"600", color:texto, margin:0 }}>{c.nombre}</p>
                  <p style={{ fontSize:"11px", color:subtexto, margin:0 }}>{c.unidad} · Factor CO₂: {c.factorCO2} kg/unidad</p>
                </div>
                <button onClick={() => toggleConsumo(c.id)} style={{ padding:"4px 10px", borderRadius:"8px", border:`1px solid ${c.activo?"#22c55e":"#e63946"}`, background:c.activo?"#22c55e15":"#e6394615", color:c.activo?"#22c55e":"#e63946", cursor:"pointer", fontSize:"11px", fontWeight:"700" }}>
                  {c.activo?"Activo":"Inactivo"}
                </button>
                {esAdmin && <button onClick={() => eliminarConsumoConfig(c.id)} style={{ background:"none", border:"none", color:"#e63946", cursor:"pointer", fontSize:"16px" }}>×</button>}
              </div>
            ))}
            <div style={{ display:"flex", gap:"8px", marginTop:"10px", flexWrap:"wrap" }}>
              <input placeholder="Nombre" onChange={e => setFormNuevoConsumo(f=>({...f,nombre:e.target.value}))} style={{...inp, marginTop:0, flex:2}}/>
              <input placeholder="Unidad (kWh, L, m³)" onChange={e => setFormNuevoConsumo(f=>({...f,unidad:e.target.value}))} style={{...inp, marginTop:0, flex:1}}/>
              <input placeholder="Factor CO₂" type="number" onChange={e => setFormNuevoConsumo(f=>({...f,factorCO2:Number(e.target.value)}))} style={{...inp, marginTop:0, flex:1}}/>
              <button onClick={agregarConsumo} style={{ padding:"8px 14px", background:Q.verde, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"13px", fontWeight:"600", flexShrink:0 }}>+ Agregar</button>
            </div>

            {/* Residuos */}
            <p style={{ fontSize:"12px", fontWeight:"700", color:subtexto, textTransform:"uppercase", letterSpacing:".1em", margin:"20px 0 10px" }}>Tipos de residuo</p>
            {configResiduos.map(r => (
              <div key={r.id} style={{ display:"flex", alignItems:"center", gap:"8px", padding:"8px 12px", background:cardBg, borderRadius:"10px", marginBottom:"6px", border:`1px solid ${borde}` }}>
                <div style={{ width:"10px", height:"10px", borderRadius:"50%", background:r.color, flexShrink:0 }}/>
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:"13px", fontWeight:"600", color:texto, margin:0 }}>{r.nombre}</p>
                  <p style={{ fontSize:"11px", color:r.tipo==="peligroso"?"#e63946":subtexto, margin:0, fontWeight:r.tipo==="peligroso"?"700":"400" }}>
                    {r.tipo==="peligroso"?"⚠️ Residuo peligroso":r.tipo==="especial"?"🔶 Especial":"♻️ Reciclable"}
                  </p>
                </div>
                <button onClick={() => toggleResiduo(r.id)} style={{ padding:"4px 10px", borderRadius:"8px", border:`1px solid ${r.activo?"#22c55e":"#e63946"}`, background:r.activo?"#22c55e15":"#e6394615", color:r.activo?"#22c55e":"#e63946", cursor:"pointer", fontSize:"11px", fontWeight:"700" }}>
                  {r.activo?"Activo":"Inactivo"}
                </button>
                {esAdmin && <button onClick={() => eliminarResiduoConfig(r.id)} style={{ background:"none", border:"none", color:"#e63946", cursor:"pointer", fontSize:"16px" }}>×</button>}
              </div>
            ))}
            <div style={{ display:"flex", gap:"8px", marginTop:"10px", flexWrap:"wrap" }}>
              <input placeholder="Nombre del residuo" onChange={e => setFormNuevoResiduo(f=>({...f,nombre:e.target.value}))} style={{...inp, marginTop:0, flex:2}}/>
              <select onChange={e => setFormNuevoResiduo(f=>({...f,tipo:e.target.value}))} style={{...inp, marginTop:0, flex:1}}>
                <option value="reciclable">♻️ Reciclable</option>
                <option value="peligroso">⚠️ Peligroso</option>
                <option value="especial">🔶 Especial</option>
              </select>
              <button onClick={agregarResiduo} style={{ padding:"8px 14px", background:Q.verde, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"13px", fontWeight:"600", flexShrink:0 }}>+ Agregar</button>
            </div>
          </div>
        )}

        {/* ── TABS ──────────────────────────────────────────── */}
        <div style={{ display:"flex", gap:"6px", marginBottom:"1.2rem", background:cardBg, borderRadius:"12px", padding:"6px", border:`1px solid ${borde}`, overflowX:"auto" }}>
          {[["dashboard","🌿 Dashboard"],["consumos","⚡ Consumos"],["residuos","🗑 Residuos"],["objetivos","🎯 Objetivos"],["aspectos","🔍 Aspectos"],["legal","⚖️ Legal"]].map(([id,label])=>(
            <button key={id} onClick={() => setTab(id)}
              style={{ flex:1, padding:"8px", borderRadius:"8px", border:"none", cursor:"pointer", fontSize:"11px", fontWeight:"700", whiteSpace:"nowrap", background:tab===id?`linear-gradient(135deg,${Q.verdeClaro},${Q.verde})`:"transparent", color:tab===id?"white":subtexto, transition:"all 0.2s", minWidth:"80px" }}>
              {label}
            </button>
          ))}
        </div>

        {/* ══ DASHBOARD ═══════════════════════════════════════ */}
        {tab==="dashboard" && (
          <div>
            {/* KPIs */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(145px,1fr))", gap:"10px", marginBottom:"1.5rem" }}>
              {[
                {label:"CO₂ este mes",      val:`${kpis.co2Mes} kg`,    color:kpis.co2Mes<100?"#22c55e":"#f59e0b", icono:"🌡️", desc:"Emisiones estimadas"},
                {label:"Residuos mes",       val:`${kpis.totalResiduosMes} kg`, color:Q.azulVivo, icono:"🗑",  desc:"Total generado"},
                {label:"Residuos peligrosos",val:`${kpis.residuosPeligrosos} kg`, color:kpis.residuosPeligrosos>0?"#e63946":"#22c55e", icono:"⚠️", desc:"Tintas y químicos"},
                {label:"Obj. en riesgo",     val:kpis.objetivosEnRiesgo, color:kpis.objetivosEnRiesgo===0?"#22c55e":"#e63946", icono:"🎯", desc:"Progreso < 50%"},
                {label:"Cumplim. legal",     val:`${kpis.cumplimientoLegal}%`, color:kpis.cumplimientoLegal>=80?"#22c55e":"#e63946", icono:"⚖️", desc:"Requisitos OK"},
              ].map(({label,val,color,icono,desc})=>(
                <div key={label} style={{ background:cardBg, borderRadius:"14px", padding:"1.2rem", border:`1px solid ${borde}`, textAlign:"center", boxShadow:`0 2px 8px ${color}15` }}>
                  <p style={{ fontSize:"22px", margin:"0 0 6px" }}>{icono}</p>
                  <p style={{ fontSize:"18px", fontWeight:"800", color, margin:"0 0 4px" }}>{val}</p>
                  <p style={{ fontSize:"11px", fontWeight:"600", color:texto, margin:"0 0 2px" }}>{label}</p>
                  <p style={{ fontSize:"10px", color:subtexto, margin:0 }}>{desc}</p>
                </div>
              ))}
            </div>

            {/* Cláusulas ISO 14001 cubiertas */}
            <div style={{ ...card, background: oscuro?"#0a1f5c":"#f0fdf4", border:`1px solid ${Q.verdeClaro}30` }}>
              <h3 style={{ fontSize:"13px", fontWeight:"700", color:Q.verde, marginBottom:"12px", textTransform:"uppercase", letterSpacing:".05em" }}>Cláusulas ISO 14001:2015 cubiertas</h3>
              {[
                {clausula:"6.1.2", desc:"Aspectos ambientales significativos", ok:aspectos.length>0},
                {clausula:"6.2",   desc:"Objetivos ambientales y planificación", ok:objetivos.length>0},
                {clausula:"9.1",   desc:"Seguimiento y medición (consumos)", ok:registrosConsumo.length>0},
                {clausula:"6.1.3", desc:"Obligaciones de cumplimiento legal", ok:legales.length>0},
              ].map(({clausula,desc,ok})=>(
                <div key={clausula} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:`0.5px solid ${borde}` }}>
                  <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                    <span style={{ fontSize:"11px", fontWeight:"700", color:Q.verde, background:"#dcfce7", padding:"2px 6px", borderRadius:"6px" }}>§ {clausula}</span>
                    <span style={{ fontSize:"12px", color:texto }}>{desc}</span>
                  </div>
                  <span style={{ fontSize:"14px" }}>{ok?"✅":"⬜"}</span>
                </div>
              ))}
            </div>

            {/* Resumen residuos por tipo */}
            {registrosResiduo.length > 0 && (
              <div style={card}>
                <h3 style={{ fontSize:"13px", fontWeight:"700", color:texto, marginBottom:"14px", textTransform:"uppercase", letterSpacing:".05em" }}>Residuos acumulados por tipo</h3>
                {configResiduos.filter(r=>r.activo).map(r => {
                  const total = registrosResiduo.filter(reg => reg.residuoId === r.id).reduce((a,reg)=>a+(Number(reg.cantidad)||0),0);
                  if (!total) return null;
                  const max = Math.max(...configResiduos.map(cr => registrosResiduo.filter(reg=>reg.residuoId===cr.id).reduce((a,reg)=>a+(Number(reg.cantidad)||0),0)));
                  return (
                    <div key={r.id} style={{ marginBottom:"10px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"4px" }}>
                        <span style={{ fontSize:"12px", color:texto, fontWeight:"600" }}>{r.nombre}</span>
                        <span style={{ fontSize:"12px", color:r.color, fontWeight:"700" }}>{total} kg</span>
                      </div>
                      <div style={{ background: oscuro?"#091840":Q.grisClaro, borderRadius:"6px", height:"8px", overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${max>0?(total/max)*100:0}%`, background:r.color, borderRadius:"6px", transition:"width 0.5s" }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ CONSUMOS ════════════════════════════════════════ */}
        {tab==="consumos" && (
          <div>
            {vistaConsumo==="nuevo" ? (
              <div style={card}>
                <h3 style={{ fontSize:"14px", fontWeight:"700", color:Q.verde, marginBottom:"16px" }}>⚡ Nuevo registro de consumo</h3>
                <span style={lbl}>Tipo de consumo</span>
                <select onChange={e=>setFormConsumo(f=>({...f,consumoId:e.target.value}))} style={inp}>
                  <option value="">— Seleccionar —</option>
                  {configConsumos.filter(c=>c.activo).map(c=><option key={c.id} value={c.id}>{c.icono||"📊"} {c.nombre} ({c.unidad})</option>)}
                </select>
                <span style={lbl}>Mes</span>
                <input type="month" defaultValue={mesActual()} onChange={e=>setFormConsumo(f=>({...f,mes:e.target.value}))} style={inp}/>
                <span style={lbl}>Cantidad</span>
                <input type="number" placeholder="0" onChange={e=>setFormConsumo(f=>({...f,cantidad:e.target.value}))} style={inp}/>
                <span style={lbl}>Observaciones</span>
                <input onChange={e=>setFormConsumo(f=>({...f,obs:e.target.value}))} style={inp}/>
                <div style={{ display:"flex", gap:"10px", marginTop:"1rem" }}>
                  <button onClick={() => setVistaConsumo("listado")} style={{ flex:1, padding:"12px", background:"none", border:`1px solid ${borde}`, borderRadius:"10px", cursor:"pointer", color:texto, fontSize:"13px" }}>Cancelar</button>
                  <button onClick={guardarConsumo} style={{ flex:2, padding:"12px", background:`linear-gradient(135deg,${Q.verdeClaro},${Q.verde})`, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"14px", fontWeight:"700" }}>💾 Guardar</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:"1rem" }}>
                  <button onClick={() => setVistaConsumo("nuevo")} style={{ padding:"7px 16px", background:Q.verde, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"13px", fontWeight:"600" }}>+ Nuevo registro</button>
                </div>
                {registrosConsumo.length===0
                  ? <div style={{ textAlign:"center", padding:"4rem" }}><p style={{ fontSize:"40px" }}>⚡</p><p style={{ fontSize:"15px", color:texto, fontWeight:"600" }}>Sin registros de consumo</p><p style={{ fontSize:"13px", color:subtexto }}>Registrá los consumos mensuales de la empresa</p></div>
                  : [...registrosConsumo].reverse().map(r => {
                      const cfg = configConsumos.find(c=>c.id===r.consumoId);
                      const co2 = (Number(r.cantidad)||0) * (cfg?.factorCO2||0);
                      return (
                        <div key={r.id} style={{ background:cardBg, borderRadius:"14px", padding:"1.2rem 1.5rem", border:`1px solid ${borde}`, marginBottom:"10px" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                            <div>
                              <span style={{ fontSize:"16px", marginRight:"8px" }}>{cfg?.icono||"📊"}</span>
                              <span style={{ fontSize:"13px", fontWeight:"700", color:texto }}>{cfg?.nombre||r.consumoId}</span>
                              <p style={{ fontSize:"12px", color:subtexto, margin:"4px 0 0" }}>Mes: {r.mes} · {r.cantidad} {cfg?.unidad}</p>
                              {r.obs && <p style={{ fontSize:"11px", color:subtexto, margin:"2px 0 0" }}>{r.obs}</p>}
                            </div>
                            <div style={{ textAlign:"right" }}>
                              {co2>0 && <p style={{ fontSize:"13px", fontWeight:"700", color:"#f59e0b", margin:0 }}>{Math.round(co2*10)/10} kg CO₂</p>}
                              <p style={{ fontSize:"11px", color:subtexto, margin:"2px 0 0" }}>Por {r.cargadoPor}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                }
              </div>
            )}
          </div>
        )}

        {/* ══ RESIDUOS ════════════════════════════════════════ */}
        {tab==="residuos" && (
          <div>
            {vistaResiduo==="nuevo" ? (
              <div style={card}>
                <h3 style={{ fontSize:"14px", fontWeight:"700", color:Q.verde, marginBottom:"16px" }}>🗑 Nuevo registro de residuo</h3>
                <span style={lbl}>Tipo de residuo</span>
                <select onChange={e=>setFormResiduo(f=>({...f,residuoId:e.target.value}))} style={inp}>
                  <option value="">— Seleccionar —</option>
                  {configResiduos.filter(r=>r.activo).map(r=><option key={r.id} value={r.id}>{r.nombre} {r.tipo==="peligroso"?"⚠️":r.tipo==="especial"?"🔶":"♻️"}</option>)}
                </select>
                <span style={lbl}>Fecha</span>
                <input type="date" defaultValue={fechaHoy()} onChange={e=>setFormResiduo(f=>({...f,fecha:e.target.value}))} style={inp}/>
                <span style={lbl}>Cantidad (kg)</span>
                <input type="number" placeholder="0" onChange={e=>setFormResiduo(f=>({...f,cantidad:e.target.value}))} style={inp}/>
                <span style={lbl}>Destino</span>
                <select onChange={e=>setFormResiduo(f=>({...f,destino:e.target.value}))} style={inp}>
                  {DESTINOS.map(d=><option key={d} value={d}>{d}</option>)}
                </select>
                <span style={lbl}>Operador / Empresa receptora</span>
                <input onChange={e=>setFormResiduo(f=>({...f,operador:e.target.value}))} style={inp} placeholder="Nombre de la empresa que retira"/>
                <span style={lbl}>Nro. de manifiesto (residuos peligrosos)</span>
                <input onChange={e=>setFormResiduo(f=>({...f,manifiesto:e.target.value}))} style={inp} placeholder="Ej: MA-2026-00123"/>
                <span style={lbl}>Observaciones</span>
                <input onChange={e=>setFormResiduo(f=>({...f,obs:e.target.value}))} style={inp}/>
                <div style={{ display:"flex", gap:"10px", marginTop:"1rem" }}>
                  <button onClick={() => setVistaResiduo("listado")} style={{ flex:1, padding:"12px", background:"none", border:`1px solid ${borde}`, borderRadius:"10px", cursor:"pointer", color:texto, fontSize:"13px" }}>Cancelar</button>
                  <button onClick={guardarResiduo} style={{ flex:2, padding:"12px", background:`linear-gradient(135deg,${Q.verdeClaro},${Q.verde})`, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"14px", fontWeight:"700" }}>💾 Guardar</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:"1rem" }}>
                  <button onClick={() => setVistaResiduo("nuevo")} style={{ padding:"7px 16px", background:Q.verde, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"13px", fontWeight:"600" }}>+ Nuevo registro</button>
                </div>
                {registrosResiduo.length===0
                  ? <div style={{ textAlign:"center", padding:"4rem" }}><p style={{ fontSize:"40px" }}>🗑</p><p style={{ fontSize:"15px", color:texto, fontWeight:"600" }}>Sin registros de residuos</p><p style={{ fontSize:"13px", color:subtexto }}>Registrá los residuos generados por la empresa</p></div>
                  : [...registrosResiduo].reverse().map(r => {
                      const cfg = configResiduos.find(c=>c.id===r.residuoId);
                      return (
                        <div key={r.id} style={{ background:cardBg, borderRadius:"14px", padding:"1.2rem 1.5rem", border:`1.5px solid ${cfg?.tipo==="peligroso"?"#e6394640":borde}`, marginBottom:"10px", borderLeft:`4px solid ${cfg?.color||borde}` }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                            <div>
                              <span style={{ fontSize:"13px", fontWeight:"700", color:texto }}>{cfg?.nombre||r.residuoId}</span>
                              {cfg?.tipo==="peligroso" && <span style={{ marginLeft:"6px", fontSize:"10px", fontWeight:"800", color:"#e63946", background:"#e6394615", padding:"1px 6px", borderRadius:"4px" }}>⚠️ PELIGROSO</span>}
                              <p style={{ fontSize:"12px", color:subtexto, margin:"4px 0 0" }}>{r.fecha} · {r.cantidad} kg · {r.destino}</p>
                              {r.operador && <p style={{ fontSize:"11px", color:subtexto, margin:"2px 0 0" }}>Operador: {r.operador}</p>}
                              {r.manifiesto && <p style={{ fontSize:"11px", color:Q.azulVivo, margin:"2px 0 0", fontWeight:"600" }}>Manifiesto: {r.manifiesto}</p>}
                            </div>
                            <p style={{ fontSize:"15px", fontWeight:"800", color:cfg?.color||texto, margin:0 }}>{r.cantidad} kg</p>
                          </div>
                        </div>
                      );
                    })
                }
              </div>
            )}
          </div>
        )}

        {/* ══ OBJETIVOS ═══════════════════════════════════════ */}
        {tab==="objetivos" && (
          <div>
            {esAdmin && (
              <div style={{ ...card, background: oscuro?"#0a1f0a":"#f0fdf4", border:`1px solid ${Q.verdeClaro}30`, marginBottom:"1rem" }}>
                <h3 style={{ fontSize:"13px", fontWeight:"700", color:Q.verde, marginBottom:"12px" }}>+ Nuevo objetivo ambiental ISO 14001 §6.2</h3>
                <span style={lbl}>Descripción del objetivo</span>
                <input onChange={e=>setFormObjetivo(f=>({...f,descripcion:e.target.value}))} style={inp} placeholder="Ej: Reducir consumo eléctrico 10% respecto al año anterior"/>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"8px" }}>
                  <div><span style={lbl}>Año</span><input type="number" defaultValue={anioActual()} onChange={e=>setFormObjetivo(f=>({...f,anio:Number(e.target.value)}))} style={inp}/></div>
                  <div><span style={lbl}>Meta</span><input onChange={e=>setFormObjetivo(f=>({...f,meta:e.target.value}))} style={inp} placeholder="Ej: -10% kWh"/></div>
                  <div><span style={lbl}>Responsable</span><input onChange={e=>setFormObjetivo(f=>({...f,responsable:e.target.value}))} style={inp}/></div>
                </div>
                <button onClick={guardarObjetivo} style={{ marginTop:"12px", width:"100%", padding:"11px", background:`linear-gradient(135deg,${Q.verdeClaro},${Q.verde})`, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"13px", fontWeight:"700" }}>Guardar objetivo</button>
              </div>
            )}
            {objetivos.length===0
              ? <div style={{ textAlign:"center", padding:"3rem" }}><p style={{ fontSize:"40px" }}>🎯</p><p style={{ fontSize:"15px", color:texto, fontWeight:"600" }}>Sin objetivos ambientales</p></div>
              : objetivos.map(o => {
                  const prog = Number(o.progreso)||0;
                  const color = prog>=80?"#22c55e":prog>=50?"#f59e0b":"#e63946";
                  return (
                    <div key={o.id} style={{ background:cardBg, borderRadius:"14px", padding:"1.5rem", border:`1px solid ${borde}`, marginBottom:"12px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"12px" }}>
                        <div style={{ flex:1 }}>
                          <p style={{ fontSize:"13px", fontWeight:"700", color:texto, margin:"0 0 4px" }}>{o.descripcion}</p>
                          <p style={{ fontSize:"12px", color:subtexto, margin:0 }}>Meta: {o.meta||"—"} · {o.anio} · Resp: {o.responsable||"—"}</p>
                        </div>
                        <div style={{ display:"flex", gap:"6px", flexShrink:0 }}>
                          <span style={{ fontSize:"14px", fontWeight:"800", color }}>{prog}%</span>
                          {esAdmin && <button onClick={() => eliminarObjetivo(o.id)} style={{ background:"none", border:"none", color:"#e63946", cursor:"pointer", fontSize:"14px" }}>×</button>}
                        </div>
                      </div>
                      <div style={{ background: oscuro?"#091840":Q.grisClaro, borderRadius:"8px", height:"10px", overflow:"hidden", marginBottom:"8px" }}>
                        <div style={{ height:"100%", width:`${prog}%`, background:`linear-gradient(90deg,${color},${color}99)`, borderRadius:"8px", transition:"width 0.5s" }}/>
                      </div>
                      {esAdmin && (
                        <input type="range" min="0" max="100" value={prog} onChange={e=>actualizarProgresoObj(o.id,e.target.value)}
                          style={{ width:"100%", accentColor:Q.verde, cursor:"pointer" }}/>
                      )}
                    </div>
                  );
                })
            }
          </div>
        )}

        {/* ══ ASPECTOS AMBIENTALES ════════════════════════════ */}
        {tab==="aspectos" && (
          <div>
            {esAdmin && (
              <div style={{ ...card, background: oscuro?"#0a1f0a":"#f0fdf4", border:`1px solid ${Q.verdeClaro}30` }}>
                <h3 style={{ fontSize:"13px", fontWeight:"700", color:Q.verde, marginBottom:"12px" }}>+ Nuevo aspecto ambiental ISO 14001 §6.1.2</h3>
                <span style={lbl}>Aspecto ambiental</span>
                <input onChange={e=>setFormAspecto(f=>({...f,aspecto:e.target.value}))} style={inp} placeholder="Ej: Generación de residuos de tintas"/>
                <span style={lbl}>Impacto ambiental asociado</span>
                <input onChange={e=>setFormAspecto(f=>({...f,impacto:e.target.value}))} style={inp} placeholder="Ej: Contaminación del suelo"/>
                <span style={lbl}>Actividad / Proceso</span>
                <input onChange={e=>setFormAspecto(f=>({...f,proceso:e.target.value}))} style={inp} placeholder="Ej: Impresión offset"/>
                <span style={lbl}>Significatividad</span>
                <div style={{ display:"flex", gap:"8px", marginTop:"6px" }}>
                  {SIGNIFICATIVIDAD.map(s => (
                    <button key={s} onClick={() => setFormAspecto(f=>({...f,significatividad:s}))}
                      style={{ flex:1, padding:"8px", borderRadius:"10px", fontSize:"12px", cursor:"pointer", fontWeight:"600", border:formAspecto.significatividad===s?`2px solid ${s==="Alta"?"#e63946":s==="Media"?"#f59e0b":"#22c55e"}`:`1px solid ${borde}`, background:formAspecto.significatividad===s?`${s==="Alta"?"#e63946":s==="Media"?"#f59e0b":"#22c55e"}22`:cardBg, color:formAspecto.significatividad===s?(s==="Alta"?"#e63946":s==="Media"?"#f59e0b":"#22c55e"):texto }}>
                      {s==="Alta"?"🔴":"s"==="Media"?"🟡":"🟢"} {s}
                    </button>
                  ))}
                </div>
                <span style={lbl}>Control operacional</span>
                <input onChange={e=>setFormAspecto(f=>({...f,control:e.target.value}))} style={inp} placeholder="Ej: Procedimiento de manejo de residuos peligrosos"/>
                <button onClick={guardarAspecto} style={{ marginTop:"12px", width:"100%", padding:"11px", background:`linear-gradient(135deg,${Q.verdeClaro},${Q.verde})`, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"13px", fontWeight:"700" }}>Guardar aspecto</button>
              </div>
            )}
            {aspectos.length===0
              ? <div style={{ textAlign:"center", padding:"3rem" }}><p style={{ fontSize:"40px" }}>🔍</p><p style={{ fontSize:"15px", color:texto, fontWeight:"600" }}>Sin aspectos registrados</p><p style={{ fontSize:"13px", color:subtexto }}>ISO 14001 §6.1.2 requiere identificar y evaluar aspectos ambientales significativos</p></div>
              : aspectos.map(a => {
                  const sigColor = a.significatividad==="Alta"?"#e63946":a.significatividad==="Media"?"#f59e0b":"#22c55e";
                  return (
                    <div key={a.id} style={{ background:cardBg, borderRadius:"14px", padding:"1.2rem 1.5rem", border:`1.5px solid ${sigColor}30`, marginBottom:"10px", borderLeft:`4px solid ${sigColor}` }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"4px" }}>
                            <span style={{ fontSize:"11px", fontWeight:"800", color:sigColor, background:`${sigColor}15`, padding:"2px 8px", borderRadius:"6px" }}>{a.significatividad}</span>
                            <span style={{ fontSize:"13px", fontWeight:"700", color:texto }}>{a.aspecto}</span>
                          </div>
                          <p style={{ fontSize:"12px", color:subtexto, margin:"0 0 2px" }}>Impacto: {a.impacto||"—"}</p>
                          <p style={{ fontSize:"12px", color:subtexto, margin:"0 0 2px" }}>Proceso: {a.proceso||"—"}</p>
                          {a.control && <p style={{ fontSize:"11px", color:Q.azulVivo, margin:0 }}>Control: {a.control}</p>}
                        </div>
                        {esAdmin && <button onClick={() => eliminarAspecto(a.id)} style={{ background:"none", border:"none", color:"#e63946", cursor:"pointer", fontSize:"16px", flexShrink:0 }}>×</button>}
                      </div>
                    </div>
                  );
                })
            }
          </div>
        )}

        {/* ══ CUMPLIMIENTO LEGAL ══════════════════════════════ */}
        {tab==="legal" && (
          <div>
            {esAdmin && (
              <div style={{ ...card, background: oscuro?"#0a1f0a":"#f0fdf4", border:`1px solid ${Q.verdeClaro}30` }}>
                <h3 style={{ fontSize:"13px", fontWeight:"700", color:Q.verde, marginBottom:"12px" }}>+ Nuevo requisito legal ISO 14001 §6.1.3</h3>
                <span style={lbl}>Requisito legal / normativo</span>
                <input onChange={e=>setFormLegal(f=>({...f,requisito:e.target.value}))} style={inp} placeholder="Ej: Ley 24.051 — Residuos peligrosos"/>
                <span style={lbl}>Organismo / Jurisdicción</span>
                <input onChange={e=>setFormLegal(f=>({...f,organismo:e.target.value}))} style={inp} placeholder="Ej: Secretaría de Ambiente provincia"/>
                <span style={lbl}>Descripción del requisito</span>
                <input onChange={e=>setFormLegal(f=>({...f,descripcion:e.target.value}))} style={inp} placeholder="Ej: Inscripción como generador de residuos peligrosos"/>
                <span style={lbl}>Estado de cumplimiento</span>
                <div style={{ display:"flex", gap:"8px", marginTop:"6px", flexWrap:"wrap" }}>
                  {Object.entries(ESTADOS_LEGAL).map(([val,{label,color}])=>(
                    <button key={val} onClick={() => setFormLegal(f=>({...f,estado:val}))}
                      style={{ padding:"7px 12px", borderRadius:"10px", fontSize:"12px", cursor:"pointer", fontWeight:"600", border:formLegal.estado===val?`2px solid ${color}`:`1px solid ${borde}`, background:formLegal.estado===val?`${color}22`:cardBg, color:formLegal.estado===val?color:texto }}>
                      {label}
                    </button>
                  ))}
                </div>
                <span style={lbl}>Fecha de verificación</span>
                <input type="date" defaultValue={fechaHoy()} onChange={e=>setFormLegal(f=>({...f,fechaVerif:e.target.value}))} style={inp}/>
                <span style={lbl}>Próxima revisión</span>
                <input type="date" onChange={e=>setFormLegal(f=>({...f,proximaRevision:e.target.value}))} style={inp}/>
                <button onClick={guardarLegal} style={{ marginTop:"12px", width:"100%", padding:"11px", background:`linear-gradient(135deg,${Q.verdeClaro},${Q.verde})`, color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"13px", fontWeight:"700" }}>Guardar requisito</button>
              </div>
            )}
            {legales.length===0
              ? <div style={{ textAlign:"center", padding:"3rem" }}><p style={{ fontSize:"40px" }}>⚖️</p><p style={{ fontSize:"15px", color:texto, fontWeight:"600" }}>Sin requisitos legales registrados</p><p style={{ fontSize:"13px", color:subtexto }}>ISO 14001 §6.1.3 requiere identificar las obligaciones de cumplimiento aplicables</p></div>
              : legales.map(l => (
                  <div key={l.id} style={{ background:cardBg, borderRadius:"14px", padding:"1.2rem 1.5rem", border:`1.5px solid ${ESTADOS_LEGAL[l.estado]?.color}30`, marginBottom:"10px", borderLeft:`4px solid ${ESTADOS_LEGAL[l.estado]?.color}` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                      <div style={{ flex:1 }}>
                        <p style={{ fontSize:"13px", fontWeight:"700", color:texto, margin:"0 0 4px" }}>{l.requisito}</p>
                        <p style={{ fontSize:"12px", color:subtexto, margin:"0 0 2px" }}>{l.organismo||"—"}</p>
                        <p style={{ fontSize:"12px", color:subtexto, margin:"0 0 4px" }}>{l.descripcion||"—"}</p>
                        <p style={{ fontSize:"11px", color:subtexto, margin:0 }}>Verificado: {l.fechaVerif||"—"} {l.proximaRevision&&`· Próxima revisión: ${l.proximaRevision}`}</p>
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"6px", flexShrink:0, marginLeft:"12px" }}>
                        <span style={{ fontSize:"12px", fontWeight:"700", color:ESTADOS_LEGAL[l.estado]?.color }}>{ESTADOS_LEGAL[l.estado]?.label}</span>
                        {esAdmin && (
                          <div style={{ display:"flex", gap:"4px" }}>
                            {Object.entries(ESTADOS_LEGAL).map(([val,{color}])=>(
                              <button key={val} onClick={() => cambiarEstadoLegal(l.id,val)}
                                style={{ width:"20px", height:"20px", borderRadius:"50%", border:`2px solid ${l.estado===val?color:"transparent"}`, background:l.estado===val?color:`${color}40`, cursor:"pointer" }}/>
                            ))}
                            <button onClick={() => eliminarLegal(l.id)} style={{ background:"none", border:"none", color:"#e63946", cursor:"pointer", fontSize:"14px", marginLeft:"4px" }}>×</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
            }
          </div>
        )}

        <p style={{ fontSize:"11px", color:subtexto, textAlign:"center", marginTop:"1rem" }}>
          ALQOS™ by QORVEX · Sustentabilidad y MA · ISO 14001:2015
        </p>
      </div>
    </div>
  );
}